import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { ChartAccount, ResidenceChartAccount, AccountType } from '../types'

const QUERY_KEY = 'chart_accounts'

export function useChartAccounts() {
  return useQuery({
    queryKey: [QUERY_KEY],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('chart_accounts')
        .select('*')
        .order('code')

      if (error) throw error
      return data as ChartAccount[]
    },
  })
}

export function useChartAccountsByType(accountType?: AccountType) {
  return useQuery({
    queryKey: [QUERY_KEY, 'type', accountType],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('chart_accounts')
        .select('*')
        .eq('account_type', accountType!)
        .order('code')

      if (error) throw error
      return data as ChartAccount[]
    },
    enabled: !!accountType,
  })
}

export function useChartAccountsByClass(classPrefix: string) {
  return useQuery({
    queryKey: [QUERY_KEY, 'class', classPrefix],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('chart_accounts')
        .select('*')
        .like('code', `${classPrefix}%`)
        .order('code')

      if (error) throw error
      return data as ChartAccount[]
    },
    enabled: !!classPrefix,
  })
}

// Comptes de charges (classe 6)
export function useExpenseAccounts() {
  return useChartAccountsByClass('6')
}

// Comptes de produits (classe 7)
export function useRevenueAccounts() {
  return useChartAccountsByClass('7')
}

// Comptes de tiers (classe 4)
export function useThirdPartyAccounts() {
  return useChartAccountsByClass('4')
}

// Comptes bancaires (classe 5)
export function useBankAccounts() {
  return useChartAccountsByClass('5')
}

// Plan comptable par résidence
export function useResidenceChartAccounts(residenceId?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, 'residence', residenceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('residence_chart_accounts')
        .select(`
          *,
          chart_account:chart_accounts(*),
          distribution_key:distribution_keys(id, name, key_type)
        `)
        .eq('residence_id', residenceId!)
        .order('chart_account(code)')

      if (error) throw error
      return data as ResidenceChartAccount[]
    },
    enabled: !!residenceId,
  })
}

export function useInitializeResidenceChartAccounts() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (residenceId: string) => {
      const { data, error } = await supabase.rpc('initialize_residence_chart_accounts', {
        p_residence_id: residenceId,
      })
      if (error) throw error
      return data as number
    },
    onSuccess: (_, residenceId) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'residence', residenceId] })
    },
  })
}

export function useUpdateResidenceChartAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      ...data
    }: Partial<ResidenceChartAccount> & { id: string }) => {
      const { data: result, error } = await supabase
        .from('residence_chart_accounts')
        .update(data)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return result as ResidenceChartAccount
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

export function useToggleResidenceChartAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { data: result, error } = await supabase
        .from('residence_chart_accounts')
        .update({ is_active: isActive })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return result as ResidenceChartAccount
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

// Activer un compte qui n'a pas encore d'entrée residence_chart_account
export function useActivateChartAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ residenceId, chartAccountId }: { residenceId: string; chartAccountId: string }) => {
      const { data: result, error } = await supabase
        .from('residence_chart_accounts')
        .insert({
          residence_id: residenceId,
          chart_account_id: chartAccountId,
          is_active: true,
          opening_balance: 0,
        })
        .select()
        .single()

      if (error) throw error
      return result as ResidenceChartAccount
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

// Toggle multiple comptes (pour propager aux enfants)
export function useBulkToggleResidenceChartAccounts() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ ids, isActive }: { ids: string[]; isActive: boolean }) => {
      const { error } = await supabase
        .from('residence_chart_accounts')
        .update({ is_active: isActive })
        .in('id', ids)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

// Créer un compte personnalisé pour une résidence
export function useCreateCustomChartAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      residenceId,
      code,
      name,
      deployToAgency,
      agencyId,
    }: {
      residenceId: string
      code: string
      name: string
      deployToAgency: boolean
      agencyId?: string
    }) => {
      if (deployToAgency && agencyId) {
        // Créer dans chart_accounts au niveau agence
        const { data: chartAccount, error: chartError } = await supabase
          .from('chart_accounts')
          .insert({
            code,
            name,
            account_type: code.startsWith('6') ? 'EXPENSE' : code.startsWith('7') ? 'REVENUE' : code.startsWith('1') ? 'LIABILITY' : 'ASSET',
            agency_id: agencyId,
            is_custom: true,
          })
          .select()
          .single()

        if (chartError) throw chartError

        // Puis créer l'association pour la résidence
        const { data: result, error } = await supabase
          .from('residence_chart_accounts')
          .insert({
            residence_id: residenceId,
            chart_account_id: chartAccount.id,
            is_active: true,
            is_custom: false,
          })
          .select()
          .single()

        if (error) throw error
        return result
      } else {
        // Créer uniquement pour cette résidence via un nouveau chart_account sans agency_id
        const { data: chartAccount, error: chartError } = await supabase
          .from('chart_accounts')
          .insert({
            code,
            name,
            account_type: code.startsWith('6') ? 'EXPENSE' : code.startsWith('7') ? 'REVENUE' : code.startsWith('1') ? 'LIABILITY' : 'ASSET',
            is_custom: true,
          })
          .select()
          .single()

        if (chartError) throw chartError

        const { data: result, error } = await supabase
          .from('residence_chart_accounts')
          .insert({
            residence_id: residenceId,
            chart_account_id: chartAccount.id,
            is_active: true,
          })
          .select()
          .single()

        if (error) throw error
        return result
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

// Supprimer un compte personnalisé
export function useDeleteCustomChartAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, chartAccountId }: { id: string; chartAccountId?: string }) => {
      // Supprimer l'association résidence
      const { error } = await supabase
        .from('residence_chart_accounts')
        .delete()
        .eq('id', id)

      if (error) throw error

      // Si c'est un compte agence personnalisé, supprimer aussi le chart_account
      if (chartAccountId) {
        const { error: chartError } = await supabase
          .from('chart_accounts')
          .delete()
          .eq('id', chartAccountId)
          .eq('is_custom', true)

        if (chartError) throw chartError
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

// Structure arborescente du plan comptable
export function useChartAccountsTree() {
  const { data: accounts, ...rest } = useChartAccounts()

  const tree = accounts
    ? buildAccountTree(accounts)
    : []

  return { data: tree, ...rest }
}

interface AccountTreeNode extends ChartAccount {
  children: AccountTreeNode[]
  level: number
}

function buildAccountTree(accounts: ChartAccount[]): AccountTreeNode[] {
  const accountMap = new Map<string, AccountTreeNode>()

  // Créer les noeuds
  accounts.forEach((account) => {
    accountMap.set(account.code, {
      ...account,
      children: [],
      level: account.code.length,
    })
  })

  // Construire l'arbre
  const roots: AccountTreeNode[] = []

  accounts.forEach((account) => {
    const node = accountMap.get(account.code)!

    // Trouver le parent (code plus court)
    let parentCode = account.code.slice(0, -1)
    while (parentCode.length > 0) {
      const parent = accountMap.get(parentCode)
      if (parent) {
        parent.children.push(node)
        break
      }
      parentCode = parentCode.slice(0, -1)
    }

    // Si pas de parent, c'est une racine
    if (parentCode.length === 0) {
      roots.push(node)
    }
  })

  return roots
}
